import type { IntlShape } from 'react-intl';
import {
  AutoCalibrationConditionType,
  AutoCalibrationRule,
  AutoCalibrationRuleCondition,
  AutoCalibrationRulePeriod,
  CalibrationEventType,
  CalibrationPeriodType,
  CalibrationPlaceholderFormatters,
  DeltaDirection,
  NAME_PLACEHOLDER,
  OnaCounter,
  RatingTarget,
  ThresholdOperator,
} from './Calibration';

// Registry of the condition types the backend evaluator understands
// (admin/api/logic/calibration_rules.py). The rules editor is driven
// entirely by this file, so a new condition type is one new entry here
// plus its backend evaluator and validator schema.

export interface ConditionFieldOption {
  id: string;
  name: string;
}

export interface ConditionFieldDefinition {
  name: string;
  label: string;
  kind: 'select' | 'multiselect' | 'number';
  options?: ConditionFieldOption[];
  minValue?: number;
}

export interface ConditionTypeDefinition {
  type: AutoCalibrationConditionType;
  label: string;
  description: string;
  // Placeholders the compiled flag text can use on top of {name}.
  placeholders: string[];
  periodTypes: CalibrationPeriodType[];
  minimumDuration: number;
  defaultTemplate: string;
  defaultCondition: AutoCalibrationRuleCondition;
  defaultPeriod: { type: CalibrationPeriodType; duration: number };
  fields: ConditionFieldDefinition[];
  summarize: (condition: AutoCalibrationRuleCondition) => string;
}

// Flat shape edited by the modal form: condition keys are spread at the
// top level next to the template and the period.
export interface CalibrationRuleFormValues {
  template: string;
  condition_type: AutoCalibrationConditionType;
  period_type: CalibrationPeriodType;
  period_duration: number | string;
  enabled: boolean;
  delta?: number | string;
  direction?: DeltaDirection;
  target?: RatingTarget;
  event?: CalibrationEventType;
  ona?: OnaCounter[];
  threshold?: number | string;
  threshold_operator?: ThresholdOperator;
}

export const PERIOD_TYPES: CalibrationPeriodType[] = [
  'cycles',
  'years',
  'quarters',
  'months',
  'days',
];

const CYCLES_ONLY: CalibrationPeriodType[] = ['cycles'];

const formatTargetName = (
  target: RatingTarget | undefined,
  intl: IntlShape
): string => {
  if (Array.isArray(target)) {
    return intl.formatMessage(
      {
        id: 'app.utils.models.calibration_rule_conditions.target.specific_ratings',
        defaultMessage: 'a rating of {ratings}',
      },
      {
        ratings: intl.formatList(target.map(String), {
          type: 'disjunction',
          style: 'long',
        }),
      }
    );
  }
  switch (target) {
    case 'positive':
      return intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.target.positive_rating',
        defaultMessage: 'an above-average rating',
      });
    case 'negative':
      return intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.target.negative_rating',
        defaultMessage: 'a below-average rating',
      });
    case 'neutral':
      return intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.target.neutral_rating',
        defaultMessage: 'an average rating',
      });
    default:
      return '';
  }
};

export const getRatingTargetOptions = (
  intl: IntlShape
): ConditionFieldOption[] => [
  {
    id: 'positive',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.target_option.positive',
      defaultMessage: 'Above average',
    }),
  },
  {
    id: 'negative',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.target_option.negative',
      defaultMessage: 'Below average',
    }),
  },
  {
    id: 'neutral',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.target_option.neutral',
      defaultMessage: 'Exactly average',
    }),
  },
];

export const getPeriodTypeOptions = (
  intl: IntlShape,
  periodTypes: CalibrationPeriodType[] = PERIOD_TYPES
): ConditionFieldOption[] => {
  const names: Record<CalibrationPeriodType, string> = {
    cycles: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.period.cycles',
      defaultMessage: 'Cycles',
    }),
    years: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.period.years',
      defaultMessage: 'Years',
    }),
    quarters: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.period.quarters',
      defaultMessage: 'Quarters',
    }),
    months: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.period.months',
      defaultMessage: 'Months',
    }),
    days: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.period.days',
      defaultMessage: 'Days',
    }),
  };
  return periodTypes.map((type) => ({ id: type, name: names[type] }));
};

export const getDirectionOptions = (
  intl: IntlShape
): ConditionFieldOption[] => [
  {
    id: 'down',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.direction.down',
      defaultMessage: 'Down',
    }),
  },
  {
    id: 'up',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.direction.up',
      defaultMessage: 'Up',
    }),
  },
  {
    id: 'both',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.direction.both',
      defaultMessage: 'Up or down',
    }),
  },
];

export const getEventOptions = (intl: IntlShape): ConditionFieldOption[] => [
  {
    id: 'hired',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.event.hired',
      defaultMessage: 'Hired',
    }),
  },
  {
    id: 'role_change',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.event.role_change',
      defaultMessage: 'Role transfer',
    }),
  },
  {
    id: 'promotion',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.event.promotion',
      defaultMessage: 'Promotion',
    }),
  },
];

// How the {direction}, {event} and {period} placeholders read inside a
// compiled flag: "up or down", "role transfer", "1 cycle".
export const getPlaceholderFormatters = (
  intl: IntlShape
): CalibrationPlaceholderFormatters => {
  const directions: Record<DeltaDirection, string> = {
    up: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.placeholder.direction.up',
      defaultMessage: 'up',
    }),
    down: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.placeholder.direction.down',
      defaultMessage: 'down',
    }),
    both: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.placeholder.direction.both',
      defaultMessage: 'up or down',
    }),
  };
  const events: Record<CalibrationEventType, string> = {
    hired: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.placeholder.event.hired',
      defaultMessage: 'hire',
    }),
    role_change: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.placeholder.event.role_change',
      defaultMessage: 'role transfer',
    }),
    promotion: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.placeholder.event.promotion',
      defaultMessage: 'promotion',
    }),
  };
  const periods: Record<
    CalibrationPeriodType,
    (period: AutoCalibrationRulePeriod) => string
  > = {
    cycles: ({ duration }) =>
      intl.formatMessage(
        {
          id: 'app.utils.models.calibration_rule_conditions.placeholder.period.cycles',
          defaultMessage: '{duration, plural, one {# cycle} other {# cycles}}',
        },
        { duration }
      ),
    years: ({ duration }) =>
      intl.formatMessage(
        {
          id: 'app.utils.models.calibration_rule_conditions.placeholder.period.years',
          defaultMessage: '{duration, plural, one {# year} other {# years}}',
        },
        { duration }
      ),
    quarters: ({ duration }) =>
      intl.formatMessage(
        {
          id: 'app.utils.models.calibration_rule_conditions.placeholder.period.quarters',
          defaultMessage:
            '{duration, plural, one {# quarter} other {# quarters}}',
        },
        { duration }
      ),
    months: ({ duration }) =>
      intl.formatMessage(
        {
          id: 'app.utils.models.calibration_rule_conditions.placeholder.period.months',
          defaultMessage: '{duration, plural, one {# month} other {# months}}',
        },
        { duration }
      ),
    days: ({ duration }) =>
      intl.formatMessage(
        {
          id: 'app.utils.models.calibration_rule_conditions.placeholder.period.days',
          defaultMessage: '{duration, plural, one {# day} other {# days}}',
        },
        { duration }
      ),
  };
  return {
    direction: (direction) => directions[direction] ?? direction,
    event: (event) => events[event] ?? event,
    period: (period) =>
      periods[period.type]?.(period) ?? `${period.duration} ${period.type}`,
  };
};

export const getOnaCounterOptions = (
  intl: IntlShape
): ConditionFieldOption[] => [
  {
    id: 'heads_ups',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.ona.heads_ups',
      defaultMessage: 'Heads ups',
    }),
  },
  {
    id: 'gold_stars',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.ona.gold_stars',
      defaultMessage: 'Outstanding impacts',
    }),
  },
  {
    id: 'advises',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.ona.advises',
      defaultMessage: 'Advice given',
    }),
  },
  {
    id: 'energizes',
    name: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.ona.energizes',
      defaultMessage: 'Energizes others',
    }),
  },
];

export const getConditionTypeDefinitions = (
  intl: IntlShape
): ConditionTypeDefinition[] => {
  const targetField: ConditionFieldDefinition = {
    name: 'target',
    label: intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.field.target',
      defaultMessage: 'Current rating',
    }),
    kind: 'select',
    options: getRatingTargetOptions(intl),
  };

  return [
    {
      type: 'delta',
      label: intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.delta.label',
        defaultMessage: 'Rating change',
      }),
      description: intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.delta.description',
        defaultMessage:
          'Flags people whose rating moved by at least the given number of levels compared with any rating in the look-back window. A minimum change of 0 flags a rating equal to an earlier one instead.',
      }),
      placeholders: ['direction', 'delta', 'period'],
      periodTypes: CYCLES_ONLY,
      minimumDuration: 2,
      defaultTemplate:
        '{name} has gone {direction} {delta} or more rating levels since last cycle.',
      defaultCondition: { type: 'delta', delta: 2, direction: 'down' },
      defaultPeriod: { type: 'cycles', duration: 2 },
      fields: [
        {
          name: 'direction',
          label: intl.formatMessage({
            id: 'app.utils.models.calibration_rule_conditions.field.direction',
            defaultMessage: 'Direction',
          }),
          kind: 'select',
          options: getDirectionOptions(intl),
        },
        {
          name: 'delta',
          label: intl.formatMessage({
            id: 'app.utils.models.calibration_rule_conditions.field.delta',
            defaultMessage: 'Minimum change (rating levels)',
          }),
          kind: 'number',
          minValue: 0,
        },
      ],
      summarize: (condition) => {
        if (condition.type !== 'delta') {
          return '';
        }
        // the evaluator ignores the direction for a zero change
        if (Number(condition.delta) === 0) {
          return intl.formatMessage({
            id: 'app.utils.models.calibration_rule_conditions.delta.summary.unchanged',
            defaultMessage: 'Same rating as an earlier cycle in the look-back',
          });
        }
        const directionName =
          condition.direction === 'both'
            ? intl.formatMessage({
                id: 'app.utils.models.calibration_rule_conditions.delta.summary.both',
                defaultMessage: 'Up or down',
              })
            : condition.direction === 'up'
              ? intl.formatMessage({
                  id: 'app.utils.models.calibration_rule_conditions.delta.summary.up',
                  defaultMessage: 'Up',
                })
              : intl.formatMessage({
                  id: 'app.utils.models.calibration_rule_conditions.delta.summary.down',
                  defaultMessage: 'Down',
                });
        return intl.formatMessage(
          {
            id: 'app.utils.models.calibration_rule_conditions.delta.summary',
            defaultMessage: '{direction} by {delta}+ rating levels',
          },
          { direction: directionName, delta: condition.delta }
        );
      },
    },
    {
      type: 'consecutive',
      label: intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.consecutive.label',
        defaultMessage: 'Consecutive ratings',
      }),
      description: intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.consecutive.description',
        defaultMessage:
          'Flags people who received the selected kind of rating in every cycle of the look-back window.',
      }),
      placeholders: ['target', 'duration', 'period'],
      periodTypes: CYCLES_ONLY,
      minimumDuration: 2,
      defaultTemplate:
        '{name} received {duration} consecutive {target} ratings.',
      defaultCondition: { type: 'consecutive', target: 'negative' },
      defaultPeriod: { type: 'cycles', duration: 2 },
      fields: [targetField],
      summarize: (condition) =>
        condition.type === 'consecutive'
          ? intl.formatMessage(
              {
                id: 'app.utils.models.calibration_rule_conditions.consecutive.summary',
                defaultMessage: 'Same kind of rating every cycle: {target}',
              },
              { target: formatTargetName(condition.target, intl) }
            )
          : '',
    },
    {
      type: 'manager_ratings',
      label: intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.manager_ratings.label',
        defaultMessage: 'Ratings given to direct reports',
      }),
      description: intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.manager_ratings.description',
        defaultMessage:
          'Flags people whose manager gave every one of their direct reports the selected kind of rating in each cycle of the look-back window. The flag is shown on each direct report, not on the manager.',
      }),
      placeholders: ['manager', 'target', 'duration', 'period'],
      periodTypes: CYCLES_ONLY,
      minimumDuration: 1,
      defaultTemplate:
        "{name}'s manager {manager} gave all direct reports a {target} rating this cycle.",
      defaultCondition: { type: 'manager_ratings', target: 'positive' },
      defaultPeriod: { type: 'cycles', duration: 1 },
      fields: [
        {
          ...targetField,
          label: intl.formatMessage({
            id: 'app.utils.models.calibration_rule_conditions.field.manager_ratings_target',
            defaultMessage: 'Ratings given to every direct report',
          }),
        },
      ],
      summarize: (condition) =>
        condition.type === 'manager_ratings'
          ? intl.formatMessage(
              {
                id: 'app.utils.models.calibration_rule_conditions.manager_ratings.summary',
                defaultMessage: 'Manager gave every direct report {target}',
              },
              { target: formatTargetName(condition.target, intl) }
            )
          : '',
    },
    {
      type: 'event',
      label: intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.event.label',
        defaultMessage: 'Recent hire, transfer or promotion',
      }),
      description: intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.event.description',
        defaultMessage:
          'Flags people who received the selected kind of rating although the chosen event happened within the look-back window. A look-back in cycles counts every event until the organization has that many earlier cycles.',
      }),
      placeholders: ['event', 'target', 'duration', 'period'],
      periodTypes: PERIOD_TYPES,
      minimumDuration: 1,
      defaultTemplate:
        '{name} was promoted last cycle but is already receiving a {target} rating.',
      defaultCondition: {
        type: 'event',
        event: 'promotion',
        target: 'positive',
      },
      defaultPeriod: { type: 'cycles', duration: 1 },
      fields: [
        {
          name: 'event',
          label: intl.formatMessage({
            id: 'app.utils.models.calibration_rule_conditions.field.event',
            defaultMessage: 'Event',
          }),
          kind: 'select',
          options: getEventOptions(intl),
        },
        targetField,
      ],
      summarize: (condition) => {
        if (condition.type !== 'event') {
          return '';
        }
        const eventNames: Record<CalibrationEventType, string> = {
          hired: intl.formatMessage({
            id: 'app.utils.models.calibration_rule_conditions.event.summary.hired',
            defaultMessage: 'Recently hired',
          }),
          role_change: intl.formatMessage({
            id: 'app.utils.models.calibration_rule_conditions.event.summary.role_change',
            defaultMessage: 'Recent role transfer',
          }),
          promotion: intl.formatMessage({
            id: 'app.utils.models.calibration_rule_conditions.event.summary.promotion',
            defaultMessage: 'Recently promoted',
          }),
        };
        return intl.formatMessage(
          {
            id: 'app.utils.models.calibration_rule_conditions.event.summary',
            defaultMessage: '{event} with {target}',
          },
          {
            event: eventNames[condition.event] ?? condition.event,
            target: formatTargetName(condition.target, intl),
          }
        );
      },
    },
    {
      type: 'ona',
      label: intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.ona.label',
        defaultMessage: 'Network signals',
      }),
      description: intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.ona.description',
        defaultMessage:
          'Flags people who received the selected kind of rating while the network signals they received across the look-back window meet the threshold.',
      }),
      placeholders: ['threshold', 'target', 'duration', 'period'],
      periodTypes: CYCLES_ONLY,
      minimumDuration: 1,
      defaultTemplate:
        '{name} got a {target} rating but also received a heads up from the network.',
      defaultCondition: {
        type: 'ona',
        ona: ['heads_ups'],
        threshold: 1,
        threshold_operator: 'gte',
        target: 'positive',
      },
      defaultPeriod: { type: 'cycles', duration: 1 },
      fields: [
        {
          name: 'ona',
          label: intl.formatMessage({
            id: 'app.utils.models.calibration_rule_conditions.field.ona',
            defaultMessage: 'Network signals to count',
          }),
          kind: 'multiselect',
          options: getOnaCounterOptions(intl),
        },
        {
          name: 'threshold_operator',
          label: intl.formatMessage({
            id: 'app.utils.models.calibration_rule_conditions.field.threshold_operator',
            defaultMessage: 'Signal count is',
          }),
          kind: 'select',
          options: [
            {
              id: 'gte',
              name: intl.formatMessage({
                id: 'app.utils.models.calibration_rule_conditions.threshold_operator.gte',
                defaultMessage: 'At least',
              }),
            },
            {
              id: 'lte',
              name: intl.formatMessage({
                id: 'app.utils.models.calibration_rule_conditions.threshold_operator.lte',
                defaultMessage: 'At most',
              }),
            },
            {
              id: 'eq',
              name: intl.formatMessage({
                id: 'app.utils.models.calibration_rule_conditions.threshold_operator.eq',
                defaultMessage: 'Exactly',
              }),
            },
          ],
        },
        {
          name: 'threshold',
          label: intl.formatMessage({
            id: 'app.utils.models.calibration_rule_conditions.field.threshold',
            defaultMessage: 'Threshold',
          }),
          kind: 'number',
          minValue: 0,
        },
        targetField,
      ],
      summarize: (condition) => {
        if (condition.type !== 'ona') {
          return '';
        }
        const counterNames = getOnaCounterOptions(intl);
        const counters = (
          Array.isArray(condition.ona) ? condition.ona : [condition.ona]
        ).map((c) => counterNames.find((o) => o.id === c)?.name ?? c);
        const operatorNames: Record<ThresholdOperator, string> = {
          gte: '≥',
          lte: '≤',
          eq: '=',
        };
        return intl.formatMessage(
          {
            id: 'app.utils.models.calibration_rule_conditions.ona.summary',
            defaultMessage: '{counters} {operator} {threshold} with {target}',
          },
          {
            counters: intl.formatList(counters, {
              type: 'conjunction',
              style: 'long',
            }),
            operator: operatorNames[condition.threshold_operator ?? 'gte'],
            threshold: condition.threshold,
            target: formatTargetName(condition.target, intl),
          }
        );
      },
    },
  ];
};

export const getConditionTypeDefinition = (
  intl: IntlShape,
  type: AutoCalibrationConditionType | string | undefined
): ConditionTypeDefinition | undefined =>
  getConditionTypeDefinitions(intl).find((d) => d.type === type);

export const getSupportedPlaceholders = (
  definition: ConditionTypeDefinition | undefined
): string[] => [NAME_PLACEHOLDER, ...(definition?.placeholders ?? [])];

export const ruleToFormValues = (
  rule: AutoCalibrationRule
): CalibrationRuleFormValues => {
  const { type, ...conditionFields } = rule.condition as Record<string, any> & {
    type: AutoCalibrationConditionType;
  };
  return {
    template: rule.template,
    condition_type: type,
    period_type: rule.period.type,
    period_duration: rule.period.duration,
    enabled: rule.enabled !== false,
    ...conditionFields,
    ...(type === 'ona'
      ? {
          ona: Array.isArray(conditionFields.ona)
            ? conditionFields.ona
            : [conditionFields.ona],
          threshold_operator: conditionFields.threshold_operator ?? 'gte',
        }
      : {}),
  };
};

const toNumber = (value: unknown): number =>
  typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));

export const formValuesToRule = (
  values: CalibrationRuleFormValues,
  definition: ConditionTypeDefinition
): AutoCalibrationRule => {
  const condition: Record<string, any> = { type: definition.type };
  for (const field of definition.fields) {
    const value = values[field.name];
    condition[field.name] = field.kind === 'number' ? toNumber(value) : value;
  }
  return {
    template: values.template.trim(),
    condition: condition as AutoCalibrationRuleCondition,
    period: {
      type: values.period_type,
      duration: toNumber(values.period_duration),
    },
    enabled: values.enabled !== false,
  };
};

export const createDefaultRule = (
  definition: ConditionTypeDefinition
): AutoCalibrationRule => ({
  template: definition.defaultTemplate,
  condition: { ...definition.defaultCondition },
  period: { ...definition.defaultPeriod },
  enabled: true,
});

export const validateRuleFormValues = (
  values: CalibrationRuleFormValues,
  definition: ConditionTypeDefinition | undefined,
  intl: IntlShape
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!definition) {
    errors.condition_type = intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.error.condition_type_required',
      defaultMessage: 'Select a condition type.',
    });
    return errors;
  }
  if (!values.template?.trim()) {
    errors.template = intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.error.template_required',
      defaultMessage: 'Enter the flag message.',
    });
  } else {
    const supported = getSupportedPlaceholders(definition);
    const unknown = Array.from(
      values.template.matchAll(/{([^}]*)}/g),
      (m) => m[1]
    ).filter((p) => !supported.includes(p));
    if (unknown.length > 0) {
      errors.template = intl.formatMessage(
        {
          id: 'app.utils.models.calibration_rule_conditions.error.unknown_placeholders',
          defaultMessage:
            'Unsupported placeholders for this condition: {placeholders}',
        },
        {
          placeholders: intl.formatList(
            unknown.map((p) => `{${p}}`),
            { type: 'conjunction', style: 'long' }
          ),
        }
      );
    }
  }
  for (const field of definition.fields) {
    const value = values[field.name];
    if (field.kind === 'number') {
      const number = toNumber(value);
      if (Number.isNaN(number) || number < (field.minValue ?? 0)) {
        errors[field.name] = intl.formatMessage(
          {
            id: 'app.utils.models.calibration_rule_conditions.error.number_minimum',
            defaultMessage: 'Enter a number of at least {minimum}.',
          },
          { minimum: field.minValue ?? 0 }
        );
      }
    } else if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      errors[field.name] = intl.formatMessage({
        id: 'app.utils.models.calibration_rule_conditions.error.field_required',
        defaultMessage: 'This field is required.',
      });
    }
  }
  if (!definition.periodTypes.includes(values.period_type)) {
    errors.period_type = intl.formatMessage({
      id: 'app.utils.models.calibration_rule_conditions.error.period_type_invalid',
      defaultMessage:
        'This look-back period is not supported by the condition.',
    });
  }
  const duration = toNumber(values.period_duration);
  if (
    Number.isNaN(duration) ||
    !Number.isInteger(duration) ||
    duration < definition.minimumDuration
  ) {
    errors.period_duration = intl.formatMessage(
      {
        id: 'app.utils.models.calibration_rule_conditions.error.duration_minimum',
        defaultMessage: 'Enter a whole number of at least {minimum}.',
      },
      { minimum: definition.minimumDuration }
    );
  }
  return errors;
};
